import React, { useState } from 'react';
import Stepper from './Stepper';
import { Button } from '../ui';

/**
 * StepperWrapper
 * - Renders the Stepper header
 * - Renders the child corresponding to the active step
 * - Provides Prev / Next / Finish buttons
 *
 * Props:
 * - steps: [{ title, description? }]
 * - initialStep (number)
 * - onFinish () => void
 * - onStepChange (index) => void
 * - children: React nodes (one child per step) or a render function
 * canProceed can be either:
 * - a function: (activeIndex) => boolean
 * - a boolean: whether Next is enabled globally
 * default: always true
 * AdditionalComponent: A React component to render below the buttons and above the step content
 */
const StepperWrapper = ({
  steps = [],
  initialStep = 0,
  onFinish,
  onStepChange,
  children,
  className = '',
  hideButtons = false,
  canProceed = () => true,
  AdditionalComponent = null,
  stepsWrapperClassName = '',
}) => {
  const [active, setActive] = useState(initialStep);

  const childArray = React.Children.toArray(children);

  const goTo = (index) => {
    const i = Math.max(0, Math.min(index, steps.length - 1));
    setActive(i);
    if (onStepChange) onStepChange(i);
  };

  const handlePrev = () => goTo(active - 1);
  const isNextAllowed = () => {
    if (typeof canProceed === 'function') return canProceed(active);
    return !!canProceed;
  };

  const handleNext = () => {
    if (!isNextAllowed()) return;

    if (active >= steps.length - 1) {
      if (onFinish) onFinish();
      return;
    }
    goTo(active + 1);
  };

  return (
    <div className={`w-full ${className}`}>
      <Stepper steps={steps} active={active} onStepClick={goTo} />
      {AdditionalComponent && AdditionalComponent}
      {!hideButtons && (
        <div className='mt-4 flex items-center justify-end  gap-3'>
          <Button
            className={`${active === 0 ? 'hidden' : 'flex'}`}
            variant='outline'
            size='md'
            onClick={handlePrev}
          >
            Back
          </Button>
          <Button variant='primary' size='md' onClick={handleNext} disabled={!isNextAllowed()}>
            {active >= steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </div>
      )}

      <div className={`mt-6 ${stepsWrapperClassName}`}>{childArray[active] ?? null}</div>
    </div>
  );
};

export default StepperWrapper;
